import React, { useEffect, useState } from "react";
import { ArrowRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

const SEEN_KEY = "ds_exitintent_seen_v1";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    if (window.innerWidth < 1024) return; // desktop only

    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        setOpen(true);
        sessionStorage.setItem(SEEN_KEY, "1");
        document.removeEventListener("mouseout", onMouseOut);
      }
    };
    const t = setTimeout(() => document.addEventListener("mouseout", onMouseOut), 4000);
    return () => { clearTimeout(t); document.removeEventListener("mouseout", onMouseOut); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      const res = await joinWaitlist(email.trim(), "exit-intent");
      toast.success(`You're #${res.position}. Welcome.`);
      setOpen(false);
      setTimeout(() => {
        document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Try again.");
    } finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div data-testid="exit-intent" role="dialog" aria-modal="true"
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 ds-fade-up"
      onClick={() => setOpen(false)}>
      <div aria-hidden className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()}
        className="ds-crosshair relative w-full max-w-md bg-[#0a0a0a] border border-neutral-800 p-8 md:p-10">
        <span className="ds-ch-bl" /><span className="ds-ch-br" />
        <button data-testid="exit-intent-close" onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 inline-flex items-center justify-center text-neutral-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#FF5000] mb-6">
          <span className="w-1.5 h-1.5 bg-[#FF5000] ds-pulse" /> Wait
        </div>
        <h3 className="text-2xl md:text-3xl text-white font-medium tracking-tight leading-tight">
          See what your market is searching for.
        </h3>
        <p className="mt-3 text-neutral-400 text-[15px] leading-relaxed">
          Drop your email and join the community — we'll send your invite straight to your inbox.
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-px bg-neutral-900 border border-neutral-800 focus-within:border-[#FF5000]">
          <input data-testid="exit-intent-email" type="email" required autoFocus
            placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-[#070707] text-white text-[15px] px-4 py-3 outline-none placeholder:text-neutral-600" />
          <button data-testid="exit-intent-submit" data-cta="true" type="submit" disabled={loading || !valid}
            className="inline-flex items-center justify-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] disabled:bg-neutral-800 text-white text-[13px] font-medium px-5 py-3 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Join the Community <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
